import { Editor } from '@tiptap/core';
import { Fragment, Node, Slice } from '@tiptap/pm/model';
import { EditorState } from '@tiptap/pm/state';
import { EditorNodeInfo } from '../../../types';
import {
  calculateFragmentDepth,
  findNodeInContent,
} from '../../../utils/node-utils';
import { RlCustomSectionName } from '../../custom-block-node-names';
import { ContentReplacementOptions } from '../commands/types';
/**
 * this function is to replace the content of a node with the content of a slice
 * we could have used node.replace method instead, but it was not handling complex node structures, like insert list item into paragraph
 * @param editor - the editor instance
 * @param node - the node to replace the content of
 * @param replacementSlice - the slice to replace the content of the node with
 * @param insertFrom - the position to insert the content from
 * @param insertTo - the position to insert the content to
 * @returns the node with the replaced content
 */
const replaceContentOfNodeWithEditor = (
  editor: Editor,
  node: Node,
  replacementSlice: Slice,
  insertFrom: number,
  insertTo: number,
) => {
  const temporaryEditor = EditorState.create({
    schema: editor.state.schema,
    doc: node,
  });

  const replacedTransaction = temporaryEditor.tr.replaceRange(
    insertFrom,
    insertTo,
    replacementSlice,
  );

  const documentAfterTransaction = temporaryEditor.apply(replacedTransaction);

  return documentAfterTransaction.doc;
};
/**
 * updates the replaceContent with meta information from the originalContent i.e parent node of certain node
 * but retains the meta information of the original content.
 * @param replaceContent - content for replacement.
 * @param source - Source content that contains the meta information.
 * @param retainMetaForNode - A function to determine if the node should retain its meta information.
 * @param selectionStart - The start position to search from the original content.
 * @param selectionEnd - The end position to search to the original content.
 */
const replaceContentWithRetainedMetaFromSource = (
  editor: Editor,
  replaceContent: Slice,
  source: Node,
  retainMetaForNode: (node: Node) => boolean,
  selectionStart: number,
  selectionEnd: number,
) => {
  let finalContentToReplace = replaceContent.content;

  replaceContent.content.descendants((node, position, parent, index) => {
    if (retainMetaForNode(node)) {
      const originalNodeInfo = findNodeInContent(
        source,
        node,
        selectionStart,
        selectionEnd,
      );

      if (originalNodeInfo) {
        // calculate the relative position to insert the partial content into the original node
        const insertFrom =
          Math.max(selectionStart, originalNodeInfo.contentStart) -
          originalNodeInfo.contentStart;
        const insertTo =
          Math.min(selectionEnd, originalNodeInfo.contentEnd) -
          originalNodeInfo.contentStart;
        const { openStart, openEnd } = calculateFragmentDepth(node.content);

        const replacementSlice = new Slice(
          Fragment.from(node.content),
          openStart,
          openEnd,
        );
        const newNode = replaceContentOfNodeWithEditor(
          editor,
          originalNodeInfo.node,
          replacementSlice,
          insertFrom,
          insertTo,
        );

        // replace the new node in the final content to replace
        finalContentToReplace = finalContentToReplace.replaceChild(
          index,
          newNode,
        );
      }

      return false;
    }

    return true;
  });
  const { openStart, openEnd } = calculateFragmentDepth(finalContentToReplace);

  return new Slice(finalContentToReplace, openStart, openEnd);
};

const checkIfNodeToBeRetainedAtPosition = (
  editor: Editor,
  position: number,
  retainMetaForNode: (node: Node) => boolean,
): EditorNodeInfo | null => {
  let found: EditorNodeInfo | null = null;

  editor.state.doc.nodesBetween(position, position, (node, start) => {
    if (retainMetaForNode(node)) {
      found = {
        node,
        start,
        end: start + node.nodeSize,
        contentStart: start + 1,
        contentEnd: start + node.content.size,
      };

      return false;
    }

    return true;
  });

  return found;
};

export const getContentWithRetainedMeta = (
  editor: Editor,
  sliceToReplace: Slice,
  from: number,
  to: number,
  options: ContentReplacementOptions,
) => {
  const finalContentToReplace = options.retainMetaForNode
    ? replaceContentWithRetainedMetaFromSource(
        editor,
        sliceToReplace,
        editor.state.doc,
        options.retainMetaForNode,
        from,
        to,
      )
    : sliceToReplace;

  const fromHasNodeToBeRetained =
    options.retainMetaForNode &&
    checkIfNodeToBeRetainedAtPosition(editor, from, options.retainMetaForNode);
  const toHasNodeToBeRetained =
    options.retainMetaForNode &&
    checkIfNodeToBeRetainedAtPosition(editor, to, options.retainMetaForNode);
  const fromPositionForReplacement = fromHasNodeToBeRetained
    ? fromHasNodeToBeRetained.start
    : from;
  const toPositionForReplacement = toHasNodeToBeRetained
    ? toHasNodeToBeRetained.end
    : to;

  return {
    content: finalContentToReplace,
    from: fromPositionForReplacement,
    to: toPositionForReplacement,
  };
};
export const retainMetaForNode = (node: Node) => {
  return node.type.name === RlCustomSectionName;
};
